import { useFormikContext } from "formik";
import debounce from "lodash/debounce";
import Slider from "rc-slider";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSettings } from "../../providers/SettingsProvider";

const DEBOUNCE_MS = 300;

function RangeSlider({
    name,
    label,
    minName,
    maxName,
    min = 0,
    max = 100,
    currency = "",
    defaultValue,
    maxDecimals = null,
    step,
}) {
    const { values, setFieldValue } = useFormikContext();
    const { settings } = useSettings();
    // Fall back to CSS var before settings load so the slider doesn't render
    // with "border: 2px solid undefined" on first paint.
    const themeColor =
        settings?.ifm_NaturalPrimaryColor || "var(--doc-natural-primary-color)";

    const formikValue = useMemo(() => {
        const v = values[name] ?? defaultValue ?? [min, max];
        if (!Array.isArray(v) || v.length !== 2) return [min, max];
        return v.map((x, i) => {
            if (x === "" || x == null) return i === 0 ? min : max;
            const n = Number(x);
            return Number.isNaN(n) ? (i === 0 ? min : max) : n;
        });
    }, [values, name, defaultValue, min, max]);

    // Local slider state for smooth dragging. Only commits to Formik on drag
    // end (onChangeComplete) so 60Hz drag frames don't trigger whole-form
    // re-renders across every filter component. May hold raw strings while the
    // user is typing in the inputs (e.g. "", "5.") — the handle reads a
    // sanitized numeric copy (sliderPos) instead.
    const [dragValue, setDragValue] = useState(formikValue);

    // When true, the handle/track CSS-transition to their new position. Enabled
    // while typing (so the handle glides to the typed value) and disabled during
    // drag (so dragging stays 1:1 with the cursor, no lag).
    const [animate, setAnimate] = useState(false);

    // Sanitize a [from, to] pair into clamped numbers for the slider handle:
    // empty / null / NaN -> the matching bound; numbers clamped to [min, max].
    const toNumeric = useCallback(
        (arr) =>
            [0, 1].map((i) => {
                const x = arr[i];
                const n = Number(x);
                if (x === "" || x == null || Number.isNaN(n)) {
                    return i === 0 ? min : max;
                }
                return Math.min(max, Math.max(min, n));
            }),
        [min, max],
    );

    // Latest dragValue, readable inside the resync effect WITHOUT making it a
    // dependency (which would re-run the effect on every keystroke).
    const dragRef = useRef(dragValue);
    dragRef.current = dragValue;

    // Adopt a formik value change ONLY when it is external (reset / programmatic).
    // Suppress our own echo: the debounced commit writes the raw input (e.g. "")
    // to formik, formikValue coerces it to a bound, and without this guard the
    // effect would copy that bound back into the just-cleared box ~300ms later.
    // If the incoming value already equals what the slider is showing, it
    // originated here — skip, so an empty/partial input is never clobbered.
    const formikKey = formikValue.join(",");
    useEffect(() => {
        const shown = toNumeric(dragRef.current);
        if (shown[0] === formikValue[0] && shown[1] === formikValue[1]) {
            return;
        }
        setDragValue(formikValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formikKey]);

    // Raw values — drive the number inputs. Can be strings while typing.
    const sliderValue = dragValue;

    // Numeric, clamped — drives the rc-slider handle only. Tracks every keystroke
    // synchronously so the handle follows typed input without snapping.
    const sliderPos = useMemo(() => toNumeric(dragValue), [dragValue, toNumeric]);

    // Slider drag: clamp to [min, max]. Slider can't drag out of range anyway.
    const commitSlider = useCallback(
        (val) => {
            const safe = [
                Math.max(min, Number(val[0] ?? min)),
                Math.min(max, Number(val[1] ?? max)),
            ];
            setFieldValue(name, safe);
            if (minName) setFieldValue(minName, safe[0]);
            if (maxName) setFieldValue(maxName, safe[1]);
        },
        [setFieldValue, name, minName, maxName, min, max],
    );

    // Text input: do NOT clamp during typing — that fights the user (typing
    // "100000" gets clamped to "100" between keystrokes). Clamp on blur.
    const commitInput = useCallback(
        (val) => {
            setFieldValue(name, val);
            if (minName) setFieldValue(minName, val[0]);
            if (maxName) setFieldValue(maxName, val[1]);
        },
        [setFieldValue, name, minName, maxName],
    );

    const debouncedInputCommit = useMemo(
        () => debounce(commitInput, DEBOUNCE_MS),
        [commitInput],
    );

    useEffect(
        () => () => debouncedInputCommit.cancel(),
        [debouncedInputCommit],
    );

    const onInput = useCallback(
        (index, raw) => {
            let v = raw; // keep raw string so partial/decimal entry works
            // Cap decimal places (e.g. carat: max 2). Truncate extra digits as
            // they're typed so a 3rd decimal simply can't be entered.
            if (maxDecimals != null && typeof v === "string" && v.includes(".")) {
                const [intPart, decPart = ""] = v.split(".");
                if (decPart.length > maxDecimals) {
                    v =
                        maxDecimals === 0
                            ? intPart
                            : `${intPart}.${decPart.slice(0, maxDecimals)}`;
                }
            }
            const next = [...sliderValue];
            next[index] = v;
            setAnimate(true); // glide the handle smoothly to the typed value
            setDragValue(next); // move the handle in real time — no jump
            debouncedInputCommit(next); // Formik write stays debounced
        },
        [sliderValue, debouncedInputCommit, maxDecimals],
    );

    const onInputBlur = useCallback(
        (index) => {
            // Flush the pending debounced write, then settle the box on the
            // clamped numbers (an emptied field shows its bound instead of
            // staying blank).
            debouncedInputCommit.flush();
            const safe = toNumeric(dragRef.current);
            // Enforce from <= to: if the pair crossed, pull the field that was
            // just edited back onto a valid bound.
            if (safe[0] > safe[1]) {
                if (index === 1) {
                    safe[1] = safe[0]; // "to" set below "from" -> raise to "from"
                } else {
                    safe[0] = safe[1]; // "from" set above "to" -> lower to "to"
                }
            }
            setDragValue(safe);
            commitSlider(safe);
        },
        [debouncedInputCommit, commitSlider, toNumeric],
    );

    return (
        <div className="flex flex-wrap mb-3 md:mb-4">
            <div className="w-full md:w-1/6 mb-3 md:mb-0">
                <div className="para-label-box">{label}</div>
            </div>
            <div className="w-full md:w-5/6">
                <div className="w-[95%] mb-5">
                    <div className="relative mb-3">
                        <Slider
                            range
                            allowCross={false}
                            min={min}
                            max={max}
                            value={sliderPos}
                            onChange={(v) => {
                                setAnimate(false); // 1:1 with cursor while dragging
                                setDragValue(v);
                            }}
                            onChangeComplete={commitSlider}
                            trackStyle={[
                                {
                                    backgroundColor: themeColor,
                                    height: 6,
                                    transition: animate
                                        ? "left 0.18s ease, width 0.18s ease"
                                        : undefined,
                                },
                            ]}
                            handleStyle={[
                                {
                                    backgroundColor: "#fff",
                                    border: `2px solid ${themeColor}`,
                                    width: 20,
                                    height: 20,
                                    marginTop: -7,
                                    transition: animate
                                        ? "left 0.18s ease"
                                        : undefined,
                                },
                                {
                                    backgroundColor: "#fff",
                                    border: `2px solid ${themeColor}`,
                                    width: 20,
                                    height: 20,
                                    marginTop: -7,
                                    transition: animate
                                        ? "left 0.18s ease"
                                        : undefined,
                                },
                            ]}
                            railStyle={{ backgroundColor: "#ccc", height: 6 }}
                        />
                    </div>
                    <div className="flex flex-wrap justify-between gap-2 md:gap-5">
                        <div className="flex items-center mt-1">
                            <label className="text-[10px] text-gray-500 mr-1">
                                from {currency}
                            </label>
                            <input
                                type="number"
                                step={step}
                                value={sliderValue[0]}
                                onChange={(e) => onInput(0, e.target.value)}
                                onBlur={() => onInputBlur(0)}
                                className="w-20 md:w-24 px-2 text-xs rounded border"
                                style={{
                                    minHeight: 25,
                                    maxHeight: 25,
                                    borderColor:
                                        "color-mix(in srgb, var(--doc-natural-primary-color), white 70%)",
                                }}
                            />
                        </div>
                        <div className="flex items-center mt-1">
                            <label className="text-[10px] text-gray-500 mr-1">
                                to {currency}
                            </label>
                            <input
                                type="number"
                                step={step}
                                value={sliderValue[1]}
                                onChange={(e) => onInput(1, e.target.value)}
                                onBlur={() => onInputBlur(1)}
                                className="w-20 md:w-24 px-2 text-xs rounded border"
                                style={{
                                    minHeight: 25,
                                    maxHeight: 25,
                                    borderColor:
                                        "color-mix(in srgb, var(--doc-natural-primary-color), white 70%)",
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default memo(RangeSlider);
